import { useEffect, useState } from 'preact/hooks';
import { PlaceholderScreen } from './PlaceholderScreen';
import {
  getMoney,
  getMoneyExport,
  createExpense,
  listAllRecordsByType,
  updateRecord,
  ApiError,
  type MoneyDashboard,
  type MoneyIncomeType,
  type MoneyExport,
  type RecordRow,
} from '../lib/api';
import { openRecordEditor, useRecordsVersion, bumpRecordsVersion } from '../lib/recordEditor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Career.css';

export interface CareerMoneyProps {
  tenantId: string;
}

type LoadState = 'loading' | 'ready' | 'error';

// The expense tax categories, matching actor_expense's picklist verbatim
// (packages/verticals/actor/src/index.ts) — the ONLY values the add form
// offers, so nothing here can drift from what the ledger will accept.
export const EXPENSE_CATEGORY_LABEL: Record<string, string> = {
  coaching_classes: 'Coaching / classes',
  headshots_reel: 'Headshots / reel',
  mileage: 'Mileage',
  travel: 'Travel',
  union_dues: 'Union dues',
  equipment: 'Equipment',
  other: 'Other',
};
const EXPENSE_CATEGORIES = Object.keys(EXPENSE_CATEGORY_LABEL);

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  session_fee: 'Session fee',
  overtime: 'Overtime',
  usage: 'Usage',
  buyout: 'Buyout',
  residual: 'Residual',
  door_fee: 'Door fee',
  other: 'Other income',
};

// A ledger keeps its cents — rows always print two decimals; only the dash
// tiles are allowed the compact form.
function money(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Compact big-number for the dash ($41k / $2.1k), matching the demo's dcell.
function moneyCompact(n: number): string {
  if (Math.abs(n) >= 1000) {
    const k = n / 1000;
    return `$${(Math.round(k * 10) / 10).toLocaleString()}k`;
  }
  return `$${Math.round(n).toLocaleString()}`;
}

// The tile severity is driven ENTIRELY by the overdue slice (serious when a
// type carries any overdue gross), never by the gold Career accent.
function tileSeverity(t: MoneyIncomeType): 'serious' | 'neutral' {
  return t.overdueGross > 0 ? 'serious' : 'neutral';
}

// ---- CSV, built client-side from the export JSON (no external lib). Two
// sections (income, then expenses) + a totals block, each a normal CSV table.
function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csvRow(cells: unknown[]): string {
  return cells.map(csvCell).join(',');
}

export function buildMoneyCsv(exp: MoneyExport): string {
  const lines: string[] = [];
  lines.push(csvRow([`Year-end export — ${exp.year}`]));
  lines.push('');
  lines.push(csvRow(['INCOME']));
  lines.push(csvRow(['Date', 'Type', 'Project', 'Gross', 'Commission %', 'Net', 'Status', 'Overdue']));
  for (const r of exp.income) {
    lines.push(
      csvRow([r.date, r.type, r.project, r.gross, r.commissionPct, r.net, r.status, r.overdue ? 'yes' : 'no'])
    );
  }
  lines.push('');
  lines.push(csvRow(['EXPENSES']));
  lines.push(csvRow(['Date', 'Category', 'Note', 'Amount', 'Deductible', 'Mileage (km)']));
  for (const r of exp.expenses) {
    lines.push(csvRow([r.date, r.category, r.note, r.amount, r.deductible ? 'yes' : 'no', r.mileageKm]));
  }
  lines.push('');
  lines.push(csvRow(['TOTALS']));
  lines.push(csvRow(['Gross income', exp.totals.totalGross]));
  lines.push(csvRow(['Net income (after commission)', exp.totals.totalNet]));
  lines.push(csvRow(['Total expenses', exp.totals.totalExpenses]));
  lines.push(csvRow(['Deductible expenses', exp.totals.totalDeductible]));
  lines.push(csvRow(['Net after deductions', exp.totals.netIncome]));
  return lines.join('\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// CAREER / MONEY — the track-and-chase surface. Dash (This yr / Received /
// Overdue), income-by-type tiles with overdue severity, expenses by tax
// category + mileage, the gold Year-end export, and a calm add-expense form.
export function CareerMoney({ tenantId }: CareerMoneyProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<MoneyDashboard | null>(null);
  const [loadFailure, setLoadFailure] = useState<ReadFailure | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  // R3 — reload the money dashboard after a payment/expense create/edit/act.
  const recordsVersion = useRecordsVersion();

  async function load() {
    setState('loading');
    try {
      const d = await getMoney(tenantId);
      setData(d);
      setState('ready');
    } catch (err) {
      setLoadFailure(describeReadFailure(err));
      setState('error');
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getMoney(tenantId);
        if (cancelled) return;
        setData(d);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setLoadFailure(describeReadFailure(err));
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion]);

  async function runExport() {
    if (!data) return;
    setExporting(true);
    setExportError(null);
    try {
      const exp = await getMoneyExport(tenantId, data.year);
      downloadCsv(`year-end-${exp.year}.csv`, buildMoneyCsv(exp));
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Could not build the export.');
    } finally {
      setExporting(false);
    }
  }

  if (state === 'loading') {
    return <PlaceholderScreen eyebrow="Money" line="One moment…" />;
  }
  if (state === 'error' || !data) {
    return (
      <div class="career">
        <PlaceholderScreen
          eyebrow="Money"
          line={loadFailure?.headline ?? "Couldn't load your money."}
          soft={loadFailure?.detail}
        />
        {loadFailure?.retryable ? (
          <button type="button" class="career__add-toggle" onClick={() => void load()}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  const nothingYet =
    data.thisYearGross === 0 &&
    data.received === 0 &&
    data.overdue === 0 &&
    data.incomeByType.length === 0 &&
    data.expensesByCategory.length === 0;

  return (
    <div class="career">
      <div class="career__head">
        <div class="career__eyebrow">Money · {data.year}</div>
        <button
          type="button"
          class="list-new-btn"
          onClick={() => openRecordEditor({ mode: 'create', typeKey: 'actor_payment' })}
          data-testid="new-payment"
        >
          + New income
        </button>
      </div>

      <div class="dash">
        <div class="dash__cell">
          <div class="dash__lab">This year</div>
          <div class="dash__n">{moneyCompact(data.thisYearGross)}</div>
        </div>
        <div class="dash__cell">
          <div class="dash__lab">Received</div>
          <div class="dash__n">{moneyCompact(data.received)}</div>
        </div>
        <div class={`dash__cell${data.overdue > 0 ? ' dash__cell--over' : ''}`}>
          <div class="dash__lab">Overdue</div>
          <div class="dash__n">{moneyCompact(data.overdue)}</div>
          {data.overdueCount > 0 && (
            <div class="dash__sub">
              {data.overdueCount} unpaid
            </div>
          )}
        </div>
      </div>

      {nothingYet && (
        <div class="career__note" data-testid="money-empty">
          Nothing outstanding right now. Income and expenses will land here as they come in.
        </div>
      )}

      {data.incomeByType.length > 0 && (
        <>
          <div class="career__grp">Income · by type</div>
          {data.incomeByType.map((t) => {
            const sev = tileSeverity(t);
            const label = PAYMENT_TYPE_LABEL[t.type] ?? t.type;
            return (
              <div class={`tile tile--${sev}`} key={t.type} data-testid="income-tile">
                <div class="tile__ic">$</div>
                <div class="tile__body">
                  <div class="tile__tt">{label}</div>
                  <div class="tile__sub">
                    {money(t.gross)} gross · {money(t.net)} net · {t.commissionPct}% agent
                    {t.overdueGross > 0 ? ` · ${money(t.overdueGross)} overdue` : ''}
                  </div>
                </div>
                {t.overdueGross > 0 && <div class="tile__word">Overdue</div>}
              </div>
            );
          })}
        </>
      )}

      <PaymentsSection tenantId={tenantId} />

      {(data.expensesByCategory.length > 0 || data.mileageKm > 0) && (
        <>
          <div class="career__grp">Expenses · tax categories</div>
          {data.expensesByCategory.map((c) => (
            <div class="career__kv" key={c.category} data-testid="expense-row">
              <span class="career__kv-k">{EXPENSE_CATEGORY_LABEL[c.category] ?? c.category}</span>
              <span class="career__kv-v">{money(c.total)}</span>
            </div>
          ))}
          {data.mileageKm > 0 && (
            <div class="career__kv" data-testid="mileage-row">
              <span class="career__kv-k">Mileage (auto)</span>
              <span class="career__kv-v">{Math.round(data.mileageKm).toLocaleString()} km</span>
            </div>
          )}
        </>
      )}

      <button
        type="button"
        class="career__export"
        onClick={() => void runExport()}
        disabled={exporting}
        data-testid="export-btn"
      >
        <div class="career__export-ic">⤓</div>
        <div class="tile__body">
          <div class="career__export-tt">{exporting ? 'Preparing export…' : 'Export year-end'}</div>
          <div class="career__export-sub">Income + categorised expenses, as a CSV</div>
        </div>
      </button>
      {exportError && <div class="career__error">{exportError}</div>}

      <AddExpense tenantId={tenantId} onAdded={() => void load()} open={formOpen} setOpen={setFormOpen} />
    </div>
  );
}

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  owed: 'Owed',
  received: 'Received',
  overdue: 'Overdue',
};

// R3 — the itemized payments ledger, the surface Huan flagged as missing an
// ACTION: each payment is a live handle. A payment that isn't in yet gets a
// one-tap "Mark received"; a received one gets "Clear" (back to owed) in case
// it was marked in error. Tapping the row opens the full edit form + actions
// (re-point its booking, delete, etc.). Reverse-chron, newest first.
function PaymentsSection({ tenantId }: { tenantId: string }) {
  const [rows, setRows] = useState<RecordRow[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<ReadFailure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    listAllRecordsByType(tenantId, 'actor_payment')
      .then((r) => {
        if (cancelled) return;
        setRows([...r].reverse());
        setLoadFailure(null);
      })
      .catch((e) => !cancelled && setLoadFailure(describeReadFailure(e)));
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion]);

  async function setStatus(id: string, status: string) {
    setBusyId(id);
    setError(null);
    try {
      await updateRecord(tenantId, id, { status });
      // Refreshes this list AND the money dashboard above (both read version).
      bumpRecordsVersion();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update the payment.');
    } finally {
      setBusyId(null);
    }
  }

  // A refused read must never delete the ledger from the screen — the
  // failure renders where the payments would have been.
  if (loadFailure) {
    return (
      <>
        <div class="career__grp">Payments</div>
        <div class="career__error">
          {loadFailure.headline} {loadFailure.detail}
        </div>
      </>
    );
  }
  if (!rows || rows.length === 0) return null;

  return (
    <>
      <div class="career__grp">Payments</div>
      {error && <div class="career__error">{error}</div>}
      {rows.map((p) => {
        const type = typeof p.fields['payment_type'] === 'string' ? (p.fields['payment_type'] as string) : null;
        const label = (type && PAYMENT_TYPE_LABEL[type]) ?? p.display_name;
        const gross = typeof p.fields['gross'] === 'number' ? (p.fields['gross'] as number) : null;
        const status = typeof p.fields['status'] === 'string' ? (p.fields['status'] as string) : 'owed';
        const received = status === 'received';
        const busy = busyId === p.id;
        return (
          <div class="payment-row" key={p.id} data-testid="payment-row">
            <button
              type="button"
              class="payment-row__main"
              onClick={() => openRecordEditor({ mode: 'edit', typeKey: 'actor_payment', id: p.id })}
            >
              <span class="payment-row__label">{label}</span>
              <span class="payment-row__meta">
                {gross !== null ? money(gross) : '—'} ·{' '}
                <span class={`payment-row__status payment-row__status--${status}`}>
                  {PAYMENT_STATUS_LABEL[status] ?? status}
                </span>
              </span>
            </button>
            <button
              type="button"
              class="payment-row__act"
              disabled={busy}
              onClick={() => void setStatus(p.id, received ? 'owed' : 'received')}
              data-testid={received ? 'payment-clear' : 'payment-mark-received'}
            >
              {busy ? '…' : received ? 'Clear' : 'Mark received'}
            </button>
          </div>
        );
      })}
    </>
  );
}

function AddExpense({
  tenantId,
  onAdded,
  open,
  setOpen,
}: {
  tenantId: string;
  onAdded: () => void;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const [category, setCategory] = useState(EXPENSE_CATEGORIES[0]!);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    const amountNum = Number(amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError('Enter an amount.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const fields: Record<string, unknown> = { category, amount: amountNum };
      if (date) fields['expense_date'] = date;
      if (note.trim()) fields['note'] = note.trim();
      await createExpense(tenantId, fields);
      setAmount('');
      setDate('');
      setNote('');
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the expense.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div class="career__add">
        <button type="button" class="career__add-toggle" onClick={() => setOpen(true)}>
          + Add expense
        </button>
      </div>
    );
  }

  return (
    <div class="career__add">
      <form class="career__form" onSubmit={submit}>
        <div class="career__field">
          <label for="exp-category">Category</label>
          <select
            id="exp-category"
            value={category}
            onChange={(e) => setCategory((e.target as HTMLSelectElement).value)}
          >
            {EXPENSE_CATEGORIES.map((c) => (
              <option value={c} key={c}>
                {EXPENSE_CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div class="career__field">
          <label for="exp-amount">Amount</label>
          <input
            id="exp-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onInput={(e) => setAmount((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="career__field">
          <label for="exp-date">Date</label>
          <input
            id="exp-date"
            type="date"
            value={date}
            onInput={(e) => setDate((e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="career__field">
          <label for="exp-note">Note</label>
          <input
            id="exp-note"
            type="text"
            value={note}
            onInput={(e) => setNote((e.target as HTMLInputElement).value)}
          />
        </div>
        {error && <div class="career__error">{error}</div>}
        <div class="career__form-actions">
          <button type="button" class="career__add-cancel" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </button>
          <button type="submit" class="career__add-submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save expense'}
          </button>
        </div>
      </form>
    </div>
  );
}
